/* eslint-disable import/no-extraneous-dependencies, no-param-reassign */
import * as Sentry from '@sentry/nextjs';
import * as SentryBrowser from '@sentry/browser';
import { Extras } from '@sentry/types';
import { NextApiRequest, NextApiResponse } from 'next';
import { AxiosError } from 'axios';
import { COMMIT_SHA, NODE_ENV, SENTRY_DSN } from './public-config';
import { ApiHandler } from '../typings/api';
import logger from './logger';

export const initErrorHandler = (): void => {
  if (SENTRY_DSN) {
    const allowUrls =
      process.env.NODE_ENV === 'production'
        ? [/^https?:\/\/(apac-southeast-1|eu-central-1|us-east-1)?.?saucelabs.com\/?(sign-up|platform-configurator)/]
        : undefined;
    Sentry.init({
      enabled: NODE_ENV === 'production' || process.env.ENABLE_SENTRY === 'true',
      environment: NODE_ENV,
      debug: NODE_ENV !== 'production',
      dsn: SENTRY_DSN,
      release: COMMIT_SHA,
      tracesSampleRate: 0,
      integrations: [
        SentryBrowser.captureConsoleIntegration({
          levels: ['error'],
        }),
      ],
      allowUrls,
      ignoreErrors: [
        // Random plugins/extensions
        'top.GLOBALS',
        'canvas.contentDocument',
        /RangeError: Array buffer allocation failed/, // NoVNC lib error
        /RangeError<anonymous> in typedArrayConstructByArrayBuffer/, // NoVNC lib error
        /TypeError.*\/jsclient-bucket3\/application2\.js/, // olark (marketing extension)
        /Request failed with status code.*/,
        /Network(\s)?Error.*/,
        /NS_ERROR_.*/, // https://developer.mozilla.org/en-US/docs/Mozilla/Errors
        /^moz-extension:\/\//,
        /Error: Loading (CSS )?chunk.*failed/,
        /timeout of 5000ms exceeded/, // access management backend service known issue
        /URIError: Failed to decode param/, // nextjs business
        /warn: [LaunchDarkly]/,
        /[GSI_LOGGER]/,
      ],
      denyUrls: [
        // Chrome extensions
        /extensions\//i,
        /^chrome:\/\//i,
      ],
    });
  }
};

export type handleMessageType = (message: string, extra?: Extras) => void;
export const handleMessage: handleMessageType = (message, extra) => {
  logger.error({ content: message, extra }, 'handleMessage');
  Sentry.captureException(new Error(message), { extra });
};

export const handleExceptionWithRequest = (req: NextApiRequest, exception: Error, extra?: Extras) => {
  // If an error is thrown right afterwards, the status code is always 500
  logger.error({ err: exception, request_url: req.url, extra }, 'HTTP Status 500');
  Sentry.captureException(exception, { extra });
  throw exception;
};

export type handleExceptionType = (err: Error, extra?: Extras) => void;
export const handleException: handleExceptionType = (exception, extra) => {
  logger.error({ err: exception, extra }, 'handleException');
  Sentry.captureException(exception, { extra });
};

export const errorHandlerMiddleware =
  (handler: ApiHandler) =>
  async (req: NextApiRequest, res: NextApiResponse): Promise<void> => {
    try {
      await handler(req, res);
    } catch (exception: any) {
      logger.error(exception, 'errorHandlerMiddleware');
      Sentry.captureException(exception, { extra: { query: req.query, body: req.body } });
      throw exception;
    }
  };

export const handleAxiosException = (err: AxiosError): void => {
  if (err.response) {
    // Request made and server responded
    logger.error(
      {
        status: err.response.status,
        request_url: err.response.config.url,
        data: err.response.data,
      },
      'Axios response error',
    );
  } else if (err.request) {
    // The request was made but no response was received
    logger.error(
      {
        // eslint-disable-next-line no-underscore-dangle
        status: err.request._currentRequest?.method,
        // eslint-disable-next-line no-underscore-dangle
        request_url: err.request._currentUrl,
      },
      'Axios request error',
    );
  } else {
    // Something happened in setting up the request that triggered an Error
    logger.info(err, 'Unexpected Axios Error');
    Sentry.captureException(err);
  }
};
